// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import BarcodeItems from "@eisinc/App/Components/Cart/CartBarcodes";
import { groupCartLines } from "@eisinc/App/Components/Cart/groupCartLines";
import GroupedLinesHeader from "@eisinc/App/Components/Cart/GroupedLinesHeading";
import Notify from "@eisinc/App/Components/Notify";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { isSafariDesktop } from "@eisinc/App/Utilities/helpers";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { CheckboxGroupComponentProps } from "@eisinc/MobiusOverrides/CheckboxGroup";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@eisinc/Overrides/Widgets/Cart/CartLineCardExpanded";
import { shipmentDateTypeValues } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { CartLineCardCondensedStyles } from "@insite/content-library/Widgets/Cart/CartLineCardCondensed";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const pageType = getCurrentPage(state)?.type;
    const isMultipleScheduleSelected =
        (state as CustomAppState).xCheckout.shipmentSchedule === shipmentDateTypeValues.ScheduledDates;

    const currentCart = getCurrentCartState(state).value;
    // XNG-Changes
    const dateNotSet =
        currentCart?.properties?.shipmentDateType !== shipmentDateTypeValues.ScheduledDates &&
        isMultipleScheduleSelected &&
        pageType === "CheckoutReviewAndSubmitPage";
    return {
        currentCart,
        session: state.context.session,
        promotionsDataView: getCurrentPromotionsDataView(state),
        settingsCollection: getSettingsCollection(state),
        isClearingCart: state.pages.cart.isClearingCart,
        // XNG-Change
        useCustomCartLinesStyle: pageType === "CartPage",
        dateNotSet,
    };
};

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

export interface ExternalProps {
    // cart padded down from other pages
    customCart?: any;
    // promotion object of other carts
    customPromotion?: PromotionModel[];
    // view options to display on the cart line
    options?: (
        | "date"
        | "poNumber"
        | "addToList"
        | "editable"
        | "inventory"
        | "badging"
        | "qtyOrderedShipped"
        | "addToCart"
        | "shipmentData"
        | "productIdAsId"
        | "dontShowCertification"
        | "showQuotedPrice"
        | "editableQty"
    )[];
    // check whether group by inventory-CartPage or warehouse-OrderSubmit
    groupBy?: "inventory" | "warehouse";
    // content to be replaced in the place of other components in the cart line
    injectedContent?: { qty?: React.ReactNode[]; availability?: React.ReactNode[]; total?: React.ReactNode[] };
    injectedDataNames?: {
        customerProductNumber?: string;
        productNumber?: string;
        qtyOne?: string;
        qtyTwo?: string;
        total?: string;
    };
    injectedColumnsLabels?: { product?: string; total?: string };
    injectedCallbacks?: { handleShowShipmentDataClick?: Function };
    pricingOnDate?: any;
    fallbackDateToDash?: boolean;
}

type Props = ExternalProps &
    Partial<OwnProps> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface PartitionedLines {
    inStockCustom?: any;
    inStock?: CartLineModel[];
    partially?: CartLineModel[];
    onBackOrder?: CartLineModel[];
    requiresQuote?: CartLineModel[];
}

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    // XNG-Change
    barcodesHeading?: InjectableCss;
    continueShoppingLink?: InjectableCss;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    headerWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    header: {
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 20px;
                `,
            },
        },
        removeAllLink: {
            css: css`
                margin-left: 24px;
            `,
        },
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: "danger.main",
            css: css`
                margin-right: 10px;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${getColor("danger.main")};
                padding: 15px 10px;
            `,
        },
    },
    footerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            margin: 16px 0;
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    currentCart,
    customCart,
    promotionsDataView,
    settingsCollection,
    fields,
    session,
    updateCartLine,
    removeCartLine,
    customPromotion,
    groupBy,
    options,
    injectedContent,
    injectedDataNames,
    injectedColumnsLabels,
    injectedCallbacks,
    pricingOnDate,
    // XNG-Change
    useCustomCartLinesStyle,
    dateNotSet,
}) => {
    // if (!customCart && !promotionsDataView.value) {
    //     return null;
    // }

    const ownGroupBy = !customCart && groupBy === undefined ? "inventory" : groupBy;
    const cart = customCart || currentCart;
    const cartLines = cart?.cartLines || cart?.quoteLineCollection || cart?.orderLines || cart?.invoiceLines;
    const showCutWire = !customCart;
    const msgs = {
        yourPart: siteMessage("Custom_Your_Part_#", "Your Part #"),
        specNumber: siteMessage("Custom_Spec_#", "Spec #"),
        mfr: siteMessage("Custom_MFR_#", "MFR #"),
        partNumber: siteMessage("Custom_Part_#", "EIS Part #"),
    };

    if (cartLines?.length === 0 && !customCart) {
        return (
            <StyledSection>
                <Clickable {...styles.continueShoppingLink} onClick={() => window.history.back()}>
                    {translate("Continue Shopping")}
                </Clickable>
                <Notify
                    messageType="warning"
                    body={
                        <Typography data-test-selector="cart_noCartLinesMessage">
                            {siteMessage("Cart_NoOrderLines")}
                        </Typography>
                    }
                />
            </StyledSection>
        );
    }

    const [lines, setLines] = React.useState({} as PartitionedLines);

    let warehouses: string[] = [];

    if (
        cart &&
        cart?.fulfillmentMethod &&
        cart?.fulfillmentMethod.toLowerCase() === "ship" &&
        ownGroupBy === "warehouse" &&
        // eslint-disable-next-line no-prototype-builtins
        cart.properties?.hasOwnProperty("optimalPlantForOrder")
    ) {
        const shipGroupingData = JSON.parse(cart.properties["optimalPlantForOrder"]);

        warehouses = [];
        shipGroupingData.forEach((item: any) => {
            if (!warehouses.includes(item.PlantDescription) && item.PlantDescription !== null) {
                warehouses.push(item.PlantDescription);
            }
        });
    }

    React.useEffect(() => {
        if (cartLines && cartLines?.length > 0 && ownGroupBy !== undefined) {
            if (
                cart &&
                cart?.fulfillmentMethod!.toLowerCase() === "ship" &&
                ownGroupBy === "warehouse" &&
                // eslint-disable-next-line no-prototype-builtins
                cart.properties?.hasOwnProperty("optimalPlantForOrder")
            ) {
                // these conditions means we are on order submit
                const shipGroupingData = JSON.parse(cart.properties["optimalPlantForOrder"]);
                setLines(
                    groupCartLines(cartLines, cart?.fulfillmentMethod, ownGroupBy === "warehouse", shipGroupingData),
                );
            } else {
                // OFA call is down or not found as GCG
                setLines(groupCartLines(cartLines, cart?.fulfillmentMethod, ownGroupBy === "warehouse"));
            }
        } else {
            setLines(groupCartLines(cartLines, cart?.fulfillmentMethod, ownGroupBy === "warehouse"));
        }
        // cart as dependency to call the inventory everytime and check against it
    }, [cart]);

    const today = new Date();
    const currentDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
    const { productSettings } = settingsCollection;
    const promotions = promotionsDataView.value || customPromotion;

    // shared functionality for grouped cart lines
    const getGroupedLines = (
        line: CartLineModel,
        promotions: PromotionModel[] | undefined,
        isInStock: boolean,
        isPartial: boolean,
        isBackOrder: boolean,
        isRequiresQuote: boolean,
        isCustomGrouping?: boolean,
        index?: number,
    ) => {
        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === line.id) : [];
        const showRemoveAction = !line.isPromotionItem && cart?.canModifyOrder;

        return (
            <CartLineContext.Provider
                value={line}
                key={`${line.id}_${
                    isPartial
                        ? "partial"
                        : isInStock
                          ? "inStock"
                          : isBackOrder
                            ? "backOrder"
                            : isRequiresQuote
                              ? "requiresQuote"
                              : undefined
                }`}
            >
                <CartLineCardExpanded
                    cart={cart!}
                    promotions={matchingPromotions}
                    productSettings={productSettings}
                    showInventoryAvailability={customCart ? options?.includes("inventory") : true}
                    showLineNotes={customCart ? true : fields?.showLineNotes}
                    extendedStyles={styles.cardExpanded}
                    showAddToList={customCart ? options?.includes("addToList") : false}
                    showRemoveAction={showRemoveAction}
                    isInStock={isInStock}
                    isPartial={isPartial}
                    isBackOrder={isBackOrder}
                    showBadging={customCart ? options?.includes("badging") : true}
                    editable={customCart ? options?.includes("editable") : true}
                    editableQty={customCart ? options?.includes("editableQty") : true}
                    showPONumber={options?.includes("poNumber")}
                    showDate={
                        (options?.includes("date") || useCustomCartLinesStyle) &&
                        cart?.fulfillmentMethod === FulfillmentMethod.Ship
                    }
                    isCustomGrouping={isCustomGrouping}
                    qtyContent={injectedContent?.qty}
                    availabilityContent={
                        injectedContent?.availability &&
                        (injectedContent.availability.length > 0
                            ? injectedContent.availability[index || 0]
                            : injectedContent.availability[0])
                    }
                    totalContent={injectedContent?.total}
                    customerProductNumber={injectedDataNames?.customerProductNumber}
                    productNumber={injectedDataNames?.productNumber}
                    showQtyOrderedShipped={options?.includes("qtyOrderedShipped")}
                    showAddToCart={options?.includes("addToCart")}
                    showShipmentData={options?.includes("shipmentData")}
                    handleShowShipmentDataClick={injectedCallbacks?.handleShowShipmentDataClick}
                    productId={line?.productId}
                    qtyOne={injectedDataNames?.qtyOne}
                    qtyTwo={injectedDataNames?.qtyTwo}
                    pricingOnDate={pricingOnDate}
                    showCutWire={showCutWire}
                    productIdAsId={options?.includes("productIdAsId")}
                    total={injectedDataNames?.total}
                    showQuotedPrice={options?.includes("showQuotedPrice")}
                    useModalForCertification={true}
                    dontShowCertification={options?.includes("dontShowCertification")}
                    fallbackDateToDash={dateNotSet}
                />
            </CartLineContext.Provider>
        );
    };

    const defaultView = cartLines?.map((line: any, index: number) => {
        return getGroupedLines(line, promotions, false, false, false, false, false, index);
    });

    const inStockCartLinesDisplay = lines.inStock?.map((line: any) => {
        return getGroupedLines(line, promotions, true, false, false, false, ownGroupBy === "warehouse");
    });

    const partiallyCartLinesDisplay = lines.partially?.map((line: any) => {
        return getGroupedLines(line, promotions, false, true, false, false, ownGroupBy === "warehouse");
    });

    const onBackOrderCartLinesDisplay = lines.onBackOrder?.map((line: any) => {
        return getGroupedLines(line, promotions, false, false, true, false, ownGroupBy === "warehouse");
    });

    const requiresQuoteCartLinesDisplay = lines.requiresQuote?.map((line: any) => {
        return getGroupedLines(line, promotions, false, false, false, true, ownGroupBy === "warehouse");
    });

    const customShip = [] as any;

    if (
        customCart &&
        cart &&
        cart?.fulfillmentMethod &&
        cart?.fulfillmentMethod.toLowerCase() === "ship" &&
        lines &&
        lines?.inStockCustom
    ) {
        // eslint-disable-next-line array-callback-return
        warehouses.map(warehouse => {
            customShip[warehouse] = [];

            if (
                lines &&
                lines.inStockCustom &&
                lines.inStockCustom[warehouse] &&
                lines?.inStockCustom[warehouse].lines.length > 0
            ) {
                // eslint-disable-next-line array-callback-return
                lines?.inStockCustom[warehouse].lines.map((line: any) => {
                    const lineComponent = getGroupedLines(
                        line.line,
                        promotions,
                        !line.isSplitted,
                        line.isSplitted,
                        false,
                        false,
                        ownGroupBy === "warehouse",
                    );
                    customShip[warehouse].push(lineComponent);
                });
            }
        });
    }

    return (
        // xng-change - adding aria-label because we have multiple sections in the same page
        <StyledSection {...styles.wrapper} aria-label={translate("Cart Lines Container")}>
            {customCart && ownGroupBy === undefined && (
                <StyledWrapper>
                    <GroupedLinesHeader
                        editable={customCart ? options?.includes("editable") && !options.includes("date") : true}
                        showQtyOrderedShipped={options?.includes("qtyOrderedShipped")}
                        injectedColumnsLabels={injectedColumnsLabels}
                        showReqDeliveryDate={
                            (useCustomCartLinesStyle || options?.includes("date")) &&
                            cart?.fulfillmentMethod === FulfillmentMethod.Ship
                        }
                    />
                    {defaultView}
                </StyledWrapper>
            )}
            {customCart &&
                ownGroupBy === "warehouse" &&
                warehouses.length > 0 &&
                warehouses.map(warehouse => {
                    return (
                        <StyledWrapper
                            key={warehouse}
                            css={css`
                                margin-bottom: 32px;
                                @media print {
                                    display: none;
                                }
                            `}
                        >
                            {customShip && customShip[warehouse] && customShip[warehouse].length > 0 && (
                                <GroupedLinesHeader
                                    isShip={cart?.fulfillmentMethod.toLowerCase() === "ship"}
                                    warehouse={warehouse}
                                    isCustom={true}
                                    editable={
                                        customCart ? options?.includes("editable") && !options.includes("date") : true
                                    }
                                    showReqDeliveryDate={
                                        (useCustomCartLinesStyle || options?.includes("date")) &&
                                        cart?.fulfillmentMethod === FulfillmentMethod.Ship
                                    }
                                />
                            )}
                            {customShip && customShip[warehouse] && customShip[warehouse].length > 0
                                ? customShip[warehouse]
                                : null}
                        </StyledWrapper>
                    );
                })}
            {lines.inStock && ownGroupBy !== undefined && lines.inStock?.length > 0 && (
                <StyledSection
                    css={css`
                        margin-bottom: 32px;
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <GroupedLinesHeader
                        isShip={cart?.fulfillmentMethod.toLowerCase() === "ship"}
                        warehouse={session?.pickUpWarehouse?.description || session?.pickUpWarehouse?.name}
                        isInStock={true}
                        editable={customCart ? options?.includes("editable") && !options.includes("date") : true}
                        showReqDeliveryDate={
                            (useCustomCartLinesStyle || options?.includes("date")) &&
                            cart?.fulfillmentMethod === FulfillmentMethod.Ship
                        }
                    />
                    {inStockCartLinesDisplay}
                </StyledSection>
            )}

            {lines.partially && ownGroupBy !== undefined && lines.partially?.length > 0 && (
                <StyledSection
                    css={css`
                        margin-bottom: 32px;
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <GroupedLinesHeader
                        isShip={cart?.fulfillmentMethod.toLowerCase() === "ship"}
                        warehouse={session?.pickUpWarehouse?.description || session?.pickUpWarehouse?.name}
                        isPartial={true}
                        editable={customCart ? options?.includes("editable") && !options.includes("date") : true}
                        showReqDeliveryDate={
                            (useCustomCartLinesStyle || options?.includes("date")) &&
                            cart?.fulfillmentMethod === FulfillmentMethod.Ship
                        }
                    />
                    {partiallyCartLinesDisplay}
                </StyledSection>
            )}

            {lines.onBackOrder && ownGroupBy !== undefined && lines.onBackOrder?.length > 0 && (
                <StyledSection
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <GroupedLinesHeader
                        isOnBackOrder={true}
                        editable={customCart ? options?.includes("editable") && !options.includes("date") : true}
                        showReqDeliveryDate={
                            (useCustomCartLinesStyle || options?.includes("date")) &&
                            cart?.fulfillmentMethod === FulfillmentMethod.Ship
                        }
                    />
                    <StyledWrapper>{onBackOrderCartLinesDisplay}</StyledWrapper>
                </StyledSection>
            )}

            {lines.requiresQuote && ownGroupBy !== undefined && lines.requiresQuote?.length > 0 && (
                <StyledSection
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                >
                    <GroupedLinesHeader
                        isRequiresQuote={true}
                        editable={customCart ? options?.includes("editable") && !options.includes("date") : true}
                        showReqDeliveryDate={
                            (useCustomCartLinesStyle || options?.includes("date")) &&
                            cart?.fulfillmentMethod === FulfillmentMethod.Ship
                        }
                    />
                    <StyledWrapper>{requiresQuoteCartLinesDisplay}</StyledWrapper>
                </StyledSection>
            )}

            {ownGroupBy === "inventory" && (
                <StyledWrapper
                    css={css`
                        position: relative;

                        @media print {
                            margin: 0 ${isSafariDesktop() ? "54px" : "25px"};
                            page-break-after: avoid;
                            break-after: avoid;
                        }

                        @media screen {
                            display: none;
                        }
                    `}
                >
                    <BarcodeItems cartLines={cartLines} msgs={msgs} />
                </StyledWrapper>
            )}
        </StyledSection>
    );
};

interface CartLinesHeaderProps {
    onRemoveAllClick: () => void;
}

const CartLinesComponent: FC<Props> = ({
    customCart,
    options,
    customPromotion,
    groupBy,
    injectedContent,
    injectedCallbacks,
    pricingOnDate,
    fallbackDateToDash = false,
    dateNotSet,
    ...otherProps
}) => {
    return (
        <CartLines
            customCart={customCart}
            groupBy={groupBy}
            options={options}
            injectedContent={injectedContent}
            injectedCallbacks={injectedCallbacks}
            customPromotion={customPromotion}
            pricingOnDate={pricingOnDate}
            fallbackDateToDash={fallbackDateToDash}
            dateNotSet={dateNotSet}
            {...otherProps}
        />
    );
};

export const CartComponent = connect(mapStateToProps, mapDispatchToProps)(CartLinesComponent);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
            },
        ],
    },
};

export default widgetModule;
